import { Network } from "lucide-react";
import { useId } from "react";

import useTranslations from "@/shared/lang/useTranslations";

import { SELL_LIMIT_KEYS } from "../../constants/cryptoSwaps.constants";
import { useAmountFormatter } from "../../hooks/useAmountFormatter";
import { useAmountIssueText } from "../../hooks/useAmountIssueText";
import type { OtcSellPair, OtcSellQuote } from "../../types/cryptoSell.types";
import { isFieldError, SELL_RANGE_KEYS, type AmountIssue } from "../../utils/cryptoSwapAmountIssues";
import type { AmountBounds } from "../../utils/cryptoSwapAmounts";
import type { ConverterField } from "../../utils/cryptoSwapConverter";
import { AmountBox, FlowSpine, IssueLine, LimitsLine } from "../swap/AmountFields";

export interface SellAmountFieldsProps {
  pair: OtcSellPair;
  sendField: ConverterField;
  receiveField: ConverterField;
  onSendChange: (value: string) => void;
  onReceiveChange: (value: string) => void;
  sendBounds: AmountBounds | null;
  /** Already narrowed to what the organization's key accepts. */
  receiveBounds: AmountBounds | null;
  sendIssue: AmountIssue | null;
  receiveIssue: AmountIssue | null;
  quote: OtcSellQuote | null;
  isPricing: boolean;
  networkLabel: string;
  disabled?: boolean;
}

/** USDT leaving the customer's wallet on top, the pesos paid to the key below. */
export function SellAmountFields({
  pair,
  sendField,
  receiveField,
  onSendChange,
  onReceiveChange,
  sendBounds,
  receiveBounds,
  sendIssue,
  receiveIssue,
  quote,
  isPricing,
  networkLabel,
  disabled,
}: SellAmountFieldsProps) {
  const { t, activeLanguage } = useTranslations("cryptoSwaps");
  const format = useAmountFormatter(pair);
  const issueText = useAmountIssueText(pair, SELL_RANGE_KEYS);
  const titleId = useId();
  const sendId = useId();
  const receiveId = useId();
  const sendHintId = useId();
  const receiveHintId = useId();

  const priceLine = quote
    ? t("price_line", {
        asset: quote.send.currency,
        price: format(quote.price.value, quote.price.currency),
        currency: quote.price.currency,
      })
    : null;

  return (
    <section aria-labelledby={titleId} className="flex flex-col gap-4">
      <h2 id={titleId} className="text-lg font-semibold tracking-tight text-foreground">
        {t("sell_amount_title")}
      </h2>

      <div className="flex flex-col">
        <AmountBox
          id={sendId}
          label={t("sell_amount_send_label")}
          labelAside={
            <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-muted/60 px-2.5 py-0.5 text-xs font-medium text-foreground">
              <Network aria-hidden="true" className="size-3.5 text-muted-foreground" />
              {networkLabel}
            </span>
          }
          currency={pair.send_currency}
          kind="ONCHAIN"
          field={sendField}
          onChange={onSendChange}
          decimals={pair.send_decimals}
          quotedMinFractionDigits={2}
          locale={activeLanguage}
          invalid={isFieldError(sendIssue)}
          describedBy={sendHintId}
          disabled={disabled}
        >
          <div id={sendHintId} className="flex flex-col gap-1">
            {sendIssue && <IssueLine issue={sendIssue}>{issueText(sendIssue)}</IssueLine>}
            <LimitsLine side="send" bounds={sendBounds} pair={pair} limitKeys={SELL_LIMIT_KEYS} />
          </div>
        </AmountBox>

        <FlowSpine pendingText={isPricing ? t("amount_pricing") : null} priceLine={priceLine} />

        <AmountBox
          id={receiveId}
          label={t("sell_amount_receive_label")}
          currency={pair.receive_currency}
          kind="FIAT"
          field={receiveField}
          onChange={onReceiveChange}
          decimals={pair.receive_decimals}
          locale={activeLanguage}
          invalid={isFieldError(receiveIssue)}
          describedBy={receiveHintId}
          disabled={disabled}
        >
          <div id={receiveHintId} className="flex flex-col gap-1">
            {receiveIssue && <IssueLine issue={receiveIssue}>{issueText(receiveIssue)}</IssueLine>}
            <LimitsLine side="receive" bounds={receiveBounds} pair={pair} limitKeys={SELL_LIMIT_KEYS} />
          </div>
        </AmountBox>
      </div>
    </section>
  );
}
